'use client'

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { Lightbox, type LightboxItem } from './lightbox'

interface LightboxContextValue {
  /** Open the shared full-screen zoom overlay on the given image. */
  open: (item: LightboxItem) => void
  /** Close the overlay (same as ×, Esc, or a backdrop tap). */
  close: () => void
  /** Whether the overlay is currently open. */
  isOpen: boolean
}

/**
 * Exported so `ZoomableImage` can detect provider presence and fall back to
 * a local overlay when rendered standalone (Storybook / isolated mounts).
 */
export const LightboxContext = createContext<LightboxContextValue | null>(null)

/**
 * Mount once near the top of the tree (inside the i18n provider). Keeps a
 * SINGLE overlay instance for the whole app, so any number of
 * `ZoomableImage`s — or imperative `useLightbox().open()` callers — share
 * one portal rather than each mounting their own.
 */
export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [item, setItem] = useState<LightboxItem | null>(null)

  const open = useCallback((next: LightboxItem) => setItem(next), [])
  const close = useCallback(() => setItem(null), [])

  const value = useMemo<LightboxContextValue>(
    () => ({ open, close, isOpen: item !== null }),
    [open, close, item]
  )

  return (
    <LightboxContext.Provider value={value}>
      {children}
      <Lightbox item={item} onClose={close} />
    </LightboxContext.Provider>
  )
}

/**
 * Imperative access to the shared overlay. Returns `null` when no provider
 * is mounted so callers (notably `ZoomableImage`) can choose a fallback;
 * direct consumers should null-check before calling `open`.
 */
export function useLightbox(): LightboxContextValue | null {
  return useContext(LightboxContext)
}
